import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, EMPTY, map, mergeMap, of, switchMap, withLatestFrom } from 'rxjs';
import { TscAdminActions, TscAdminReducer, TscAdminSelectors } from '.';
import { TscAdminService } from '../services/admin.service';

@Injectable()
export class TscWorkshopEffects {
  private store = inject<Store<TscAdminReducer.TscAdminState>>(Store);
  private actions$ = inject(Actions);
  apiService = inject(TscAdminService);

  getWorkshops$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TscAdminActions.GET_WORKSHOPS),
      map((action: TscAdminActions.GetWorkshops) => action.payload),
      switchMap(payload =>
        this.apiService.loadWorkshops(payload).pipe(
          map(response =>
            response.error ? new TscAdminActions.GetWorkshopsFailure(response.error) : new TscAdminActions.GetWorkshopsSuccess(response)
          ),
          catchError(error => of(new TscAdminActions.GetWorkshopsFailure(error)))
        )
      )
    )
  );

  getWorkshop$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TscAdminActions.GET_WORKSHOP),
      map((action: TscAdminActions.GetWorkshop) => action.payload),
      mergeMap(payload =>
        of(payload).pipe(
          withLatestFrom(this.store.select(TscAdminSelectors.getWorkshopById(payload.workshopId))),
          switchMap(([actionPayload, storeDataSet]) => {
            if (actionPayload?.force || !storeDataSet) {
              // request against REST API has to be executed
              return this.apiService.getWorkshop(payload.workshopId).pipe(
                map(response =>
                  response.error ? new TscAdminActions.GetWorkshopFailure(response.error) : new TscAdminActions.GetWorkshopSuccess(response)
                ),
                catchError(error => of(new TscAdminActions.GetWorkshopFailure(error)))
              );
            }
            return EMPTY;
          })
        )
      )
    )
  );

  updateWorkshop$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TscAdminActions.UPDATE_WORKSHOP),
      map((action: TscAdminActions.UpdateWorkshop) => action.payload),
      switchMap(payload =>
        this.apiService.updateWorkshop(payload).pipe(
          switchMap(response =>
            response.error
              ? [new TscAdminActions.UpdateWorkshopFailure(response.error, payload)]
              : [
                  new TscAdminActions.UpdateWorkshopSuccess(response, payload),
                  new TscAdminActions.GetWorkshop({ workshopId: payload.aggregateId, force: true }),
                ]
          ),

          catchError(error => of(new TscAdminActions.UpdateWorkshopFailure(error, payload)))
        )
      )
    )
  );
}
